/**
 * Counting by voice: turning a phrase into a count
 * ------------------------------------------------
 * The phrase comes from the browser's speech recognition, from the keyboard's dictation
 * (the microphone key on an iPad keyboard), or is just typed — this only ever sees text.
 *
 *   "Coors Light 53"                     → 53
 *   "Coors Light two boxes and five"     → 2 boxes + 5 singles
 *   "Crown Royal twenty three point six" → 23.6
 *   "Blue Curacao none"                  → 0
 *   "scratch that"                       → undo the last one
 *
 * Numbers can be words ("twenty four", "one hundred twelve"), digits ("24"), or a mix. Then
 * the drink is found among the area's drinks by its name, allowing for small differences in
 * how the recognizer spells it ("guinness" for "Guiness").
 */

const UNITS: Record<string, number> = { zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9 };
const TEENS: Record<string, number> = { ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19 };
const TENS: Record<string, number> = { twenty: 20, thirty: 30, forty: 40, fourty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90 };
/** Words a recognizer hears for numbers, only treated as numbers inside a number ("fifty for" → 54). */
const HOMOPHONES: Record<string, string> = { to: 'two', too: 'two', for: 'four', won: 'one', ate: 'eight' };
const OH = new Set(['oh', 'o']);
const isDigits = (t: string | undefined) => !!t && /^\d+(\.\d+)?$/.test(t);

export function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/(\d),(\d{3})/g, '$1$2')
    .replace(/[“”"!?;:,()]/g, ' ')
    .replace(/(\D)\.|\.(\D|$)/g, '$1 $2')
    .replace(/-/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

function normalizeNumberTokens(tokens: string[]): string[] {
  return tokens.map((t, i) => {
    const h = HOMOPHONES[t];
    const prev = tokens[i - 1];
    // Only right after "point" or "hundred", where a number has to follow ("point for" → .4).
    return h && (prev === 'point' || prev === 'hundred') ? h : t;
  });
}

const isSmall = (t: string | undefined) => !!t && (t in UNITS || t in TEENS || t in TENS);

/**
 * Whether `token` carries on the number before it. Two numbers said one after the other are
 * two numbers ("O sixteen twelve" is O16, then 12) — never glued into 1612.
 */
function continuesNumber(token: string, prev: string, afterPoint: boolean): boolean {
  if (afterPoint) return token in UNITS || OH.has(token) || /^\d+$/.test(token);
  if (token === 'point') return true;
  if (token === 'hundred' || token === 'thousand') return isSmall(prev) || isDigits(prev);
  if (token === 'and') return prev === 'hundred' || prev === 'thousand';
  if (prev === 'hundred' || prev === 'thousand' || prev === 'and') return isSmall(token) || isDigits(token);
  // "twenty four"
  return prev in TENS && token in UNITS && UNITS[token]! > 0;
}

interface Span {
  start: number;
  /** Exclusive. */
  end: number;
  value: number;
}

function findNumberSpans(tokens: string[]): Span[] {
  const spans: Span[] = [];
  let i = 0;
  while (i < tokens.length) {
    const t = tokens[i]!;
    if (!(isSmall(t) || isDigits(t))) {
      i++;
      continue;
    }
    let j = i + 1;
    let afterPoint = false;
    while (j < tokens.length && continuesNumber(tokens[j]!, tokens[j - 1]!, afterPoint)) {
      if (tokens[j] === 'point') afterPoint = true;
      j++;
    }
    // A trailing "point" or "and" isn't part of the number.
    while (j > i + 1 && (tokens[j - 1] === 'point' || tokens[j - 1] === 'and')) j--;
    const value = evaluateNumber(tokens.slice(i, j));
    if (value != null) spans.push({ start: i, end: j, value });
    i = j;
  }
  return spans;
}

function evaluateNumber(raw: string[]): number | null {
  const tokens = raw.filter((t) => t !== 'and');
  const at = tokens.indexOf('point');
  if (at >= 0) {
    const whole = at === 0 ? 0 : evaluateInteger(tokens.slice(0, at));
    const decimals = tokens.slice(at + 1).map((t) => (t in UNITS ? String(UNITS[t]) : OH.has(t) ? '0' : /^\d+$/.test(t) ? t : null));
    if (whole == null || decimals.length === 0 || decimals.some((d) => d == null)) return null;
    return Number(`${whole}.${decimals.join('')}`);
  }
  return evaluateInteger(tokens);
}

function evaluateInteger(tokens: string[]): number | null {
  if (tokens.length === 0) return null;
  let result = 0;
  let chunk = 0;
  for (const t of tokens) {
    if (isDigits(t)) chunk += Number(t);
    else if (t in TENS) chunk += TENS[t]!;
    else if (t in TEENS) chunk += TEENS[t]!;
    else if (t in UNITS) chunk += UNITS[t]!;
    else if (t === 'hundred') chunk = (chunk || 1) * 100;
    else if (t === 'thousand') {
      result += (chunk || 1) * 1000;
      chunk = 0;
    } else return null;
  }
  return result + chunk;
}

export type VoiceCommand =
  | { kind: 'undo' }
  | { kind: 'count'; drinkText: string; boxes: number | null; singles: number | null; total: number | null }
  | { kind: 'unknown'; reason: string };

const BOX_WORDS = new Set(['box', 'boxes', 'case', 'cases', 'flat', 'flats']);
const SINGLE_WORDS = new Set(['single', 'singles', 'bottle', 'bottles', 'can', 'cans', 'loose', 'oz', 'ounce', 'ounces']);
const ZERO_WORDS = /^(.*?)\s+(?:none|nothing|zero|all out|out|empty)$/;
const FILLER = new Set(['and', 'um', 'uh', 'okay', 'ok', 'so', 'is', 'of', 'the', 'we', 'have', 'got', 'there', 'are', 'about', 'at', 'for', 'to']);
const UNDO = /^(undo|undo that|scratch that|delete that|cancel that|go back|oops)$/;

const clean = (tokens: string[]) => {
  const t = [...tokens];
  while (t.length && FILLER.has(t[t.length - 1]!)) t.pop();
  while (t.length && FILLER.has(t[0]!)) t.shift();
  return t.join(' ');
};

/** Reads one spoken (or typed) count. */
export function parseVoiceCount(text: string): VoiceCommand {
  const tokens = normalizeNumberTokens(tokenize(text));
  const joined = tokens.join(' ');
  if (!joined) return { kind: 'unknown', reason: 'Nothing was heard.' };
  if (UNDO.test(joined)) return { kind: 'undo' };

  const zero = ZERO_WORDS.exec(joined);
  if (zero && findNumberSpans(zero[1]!.split(' ')).every((s) => s.end < zero[1]!.split(' ').length)) {
    const drinkText = clean(zero[1]!.split(' '));
    if (drinkText) return { kind: 'count', drinkText, boxes: null, singles: null, total: 0 };
  }

  const spans = findNumberSpans(tokens);
  type Q = { value: number; word: string | null; start: number };
  const qs: Q[] = [];
  spans.forEach((s, k) => {
    const after = tokens[s.end];
    if (after && (BOX_WORDS.has(after) || SINGLE_WORDS.has(after))) qs.push({ value: s.value, word: after, start: s.start });
    else if (k === spans.length - 1 && s.end === tokens.length) qs.push({ value: s.value, word: null, start: s.start });
  });
  if (qs.length === 0) return { kind: 'unknown', reason: spans.length ? 'Heard a drink but not how many. Say the number last, like “Coors Light 24”.' : 'Didn’t hear a number. Say the drink, then how many.' };
  const drinkText = clean(tokens.slice(0, qs[0]!.start));
  if (!drinkText) return { kind: 'unknown', reason: 'Didn’t hear which drink. Say the drink first, then how many.' };

  let boxes: number | null = null;
  let singles: number | null = null;
  let total: number | null = null;
  for (const q of qs) {
    if (q.word && BOX_WORDS.has(q.word)) boxes = (boxes ?? 0) + q.value;
    else if (q.word && SINGLE_WORDS.has(q.word)) singles = (singles ?? 0) + q.value;
    // A bare number after boxes is the singles ("two boxes and five"); on its own it's the total.
    else if (boxes != null) singles = (singles ?? 0) + q.value;
    else total = q.value;
  }
  return { kind: 'count', drinkText, boxes, singles, total };
}

// ---------------------------------------------------------------------------
// Finding the drink
// ---------------------------------------------------------------------------

/** Lowercase words, number words as digits ("corona zero" → "corona 0"). */
function nameTokens(text: string): string[] {
  const tokens = tokenize(text.replace(/&/g, ' and ').replace(/['’]/g, ''));
  const spans = findNumberSpans(tokens);
  const out: string[] = [];
  let i = 0;
  for (const s of spans) {
    out.push(...tokens.slice(i, s.start), String(s.value));
    i = s.end;
  }
  out.push(...tokens.slice(i));
  return out.filter((t) => t !== 'and');
}

function similarity(a: string, b: string): number {
  if (a === b) return 1;
  const m = a.length;
  const n = b.length;
  if (!m || !n) return 0;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) cur[j] = Math.min(prev[j]! + 1, cur[j - 1]! + 1, prev[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
    prev = cur;
  }
  return 1 - prev[n]! / Math.max(m, n);
}

export interface DrinkMatch<T> {
  item: T;
  score: number;
}

/**
 * The drinks whose names best fit what was said, best first. Every word said has to fit a
 * word of the name (spelling slips allowed); names that are fully said score higher.
 */
export function matchDrinks<T extends { name: string }>(said: string, items: T[]): DrinkMatch<T>[] {
  const words = nameTokens(said);
  if (words.length === 0) return [];
  const saidJoined = words.join('');
  return items
    .map((item) => {
      const nameWords = nameTokens(item.name);
      if (!nameWords.length) return { item, score: 0 };
      const fit = words.reduce((s, w) => s + Math.max(...nameWords.map((n) => similarity(w, n))), 0) / words.length;
      const covered = nameWords.reduce((s, n) => s + Math.max(...words.map((w) => similarity(w, n))), 0) / nameWords.length;
      // The whole name run together catches words split or joined differently ("o sixteen" / "O16"),
      // but counts for a little less than word-by-word, so an extra word said still matters.
      const whole = similarity(saidJoined, nameWords.join(''));
      return { item, score: Math.max(whole * 0.9, fit * 0.7 + covered * 0.3) };
    })
    .filter((m) => m.score > 0.5)
    .sort((a, b) => b.score - a.score);
}

/** The one drink meant, or null when it isn't clear (then the closest few are offered). */
export function pickDrink<T extends { name: string }>(matches: DrinkMatch<T>[]): T | null {
  const [best, second] = matches;
  if (!best || best.score < 0.72) return null;
  if (second && best.score - second.score < 0.08 && best.score < 0.98) return null;
  return best.item;
}
